// React
import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate, useOutletContext, Outlet, useParams } from 'react-router-dom'

// Antd and MDI
import { Layout, Form, notification } from 'antd'
// Components
import SearchHeader from '../layout/SearchHeader'
import DynamicTable from '../../containers/DynamicTable'
import Pagination from '../utility/Pagination'
import TableHeader from '../utility/TableHeader'

// Utils
import { PagesEnum, SearchSegmentsEnum } from '../../utils/Enums'
import { Loader } from '../../utils/Functions'
import { userManagementColumns } from '../../utils/TableColumns'
// Recoil
import { biltyOpsSelector, userTypesOptionsSelector, dataByIDSelector } from '../../recoil/selectors'
import { useRecoilValue } from 'recoil'

// Hooks
import useFetchQuery from '../../hooks/useFetchQuery'

// ERROR BOUNDARY
import { ErrorBoundary } from 'react-error-boundary'
import FallBackUI from '../errors/FallBackUI'
import { filterDataFunction } from '../../utils/Functions'
import { LevelContext } from '../../services/LevelContext'
import { useAssignVicinities } from '../../hooks/modalFinishHooks'

const { Footer, Content } = Layout

const UserManagementTable = () => {
	const { id, modalType } = useParams()
	const { collection } = useOutletContext()
	const segment = useMemo(() => Object.values(PagesEnum[collection]?.allowedSegments)[0], [collection])

	const navigator = useNavigate()

	const [loading, setLoading] = useState(false)
	const [promiseLoading, setPromiseLoading] = useState(false)
	const [finishFunction] = useAssignVicinities()
	const [keyword, setKeyword] = useState('')

	const [form] = Form.useForm()
	const [changedUserId, setChangedUserId] = useState('')
	const [pageInfo, setPageInfo] = useState({ pageNo: 1, pageSize: 10 })
	const [searchData, setSearchData] = useState({ data: [], collection: '' })
	const [filterProperty, setFilterProperty] = useState(SearchSegmentsEnum[segment]?.filterProperties?.[segment]?.[0])
	const [filteredOrdersCount, setFilteredOrdersCount] = useState(0)

	const modalData = useRecoilValue(dataByIDSelector({ collection, id: id?.split('+') }))

	const collectionData = useRecoilValue(biltyOpsSelector)
	const userTypes = useRecoilValue(userTypesOptionsSelector)

	useFetchQuery({
		segment: 'UserTypes',
		action: 'normal',
	})

	useFetchQuery({
		segment: 'Regions',
		action: 'normal',
	})

	const queryResult = useFetchQuery({
		segment: 'BiltyOps',
		action: 'normal',
		subscription: true,
	})

	useEffect(() => {
		setLoading(queryResult?.loading)
	}, [collection, queryResult]) // eslint-disable-line

	useEffect(() => {
		setPageInfo({ pageNo: 1, pageSize: 10 })
	}, [segment]) // eslint-disable-line

	useEffect(() => {
		setPageInfo((prev) => ({ ...prev, pageNo: 1 }))
	}, [keyword]) // eslint-disable-line

	useEffect(() => {
		const FilteredData = filterDataFunction({
			keyword,
			segment,
			filterProperty,
			FilteredOrders: collectionData,
		})
		setFilteredOrdersCount(FilteredData?.length)
		setSearchData({
			data: FilteredData?.slice(pageInfo.pageNo * pageInfo.pageSize - pageInfo.pageSize, pageInfo.pageNo * pageInfo.pageSize),
			collection: collection,
		})
	}, [collectionData, keyword, filterProperty, segment, collection, pageInfo])

	const handleChange = (changedValues) => {
		const id = Object.keys(changedValues)?.[0]
		const biltyUserTypeId = Object.values(changedValues)?.[0]?.biltyUserTypeId
		const biltyUserType = userTypes?.find((item) => item?.key === biltyUserTypeId)?.value
		setChangedUserId(biltyUserTypeId)
		if ('BaseManager' === biltyUserType) {
			navigator(`/userManagement/user/${id}/assignVicinities`)
		} else {
			form
				.validateFields()
				.then(async () => {
					setPromiseLoading({ [id]: true })
					return await finishFunction({
						segment,
						props: {
							biltyUserTypeId: biltyUserTypeId,
							data: {
								record: collectionData?.filter((item) => item?._id === id)?.[0],
							},
						},
					})
						.then((response) => response)
						.catch((e) => ({ type: 'error', message: 'Error', description: e.message }))
				})
				.then((queryresult) => {
					if (queryresult?.type) {
						notification[queryresult?.type]({
							message: queryresult.message,
							description: queryresult.description,
							duration: '5',
						})
					}
					setPromiseLoading(false)
				})
				.catch((e) => {
					console.log(e)
				})
		}
	}

	return (
		<>
			<Layout className='!bg-secondaryBackground'>
				<SearchHeader
					segment={segment}
					keyword={keyword}
					collection={collection}
					setKeyword={setKeyword}
					collectionData={collectionData}
					filterProperty={filterProperty}
					setFilterProperty={setFilterProperty}
				/>
				{loading ? (
					<Loader />
				) : (
					<>
						<Layout className='!bg-white rounded-lg overflow-y-hidden shadow-[0_1px_8px_rgba(0,0,0,0.16)]'>
							<Content>
								<TableHeader segment={segment} filteredCount={filteredOrdersCount} totalCount={collectionData?.length} collection={collection} />
								<ErrorBoundary
									fallbackRender={({ error, resetErrorBoundary }) => (
										<FallBackUI component={'DynamicTable DynamicTable'} error={error} resetErrorBoundary={resetErrorBoundary} />
									)}
									resetKeys={[searchData]}
								>
									<Form form={form} onValuesChange={handleChange}>
										<DynamicTable
											segment={segment}
											searchData={searchData}
											collection={collection}
											rowClassName='!h-12'
											tableHeight='calc(100vh - 360px)'
											columns={userManagementColumns}
											columnProps={{
												userTypes,
												promiseLoading,
											}}
										/>
									</Form>
								</ErrorBoundary>
							</Content>
							<Footer className='flex justify-center items-center !h-16 !bg-white !py-2 !border-t-[0.5px] !border-[rgba(0, 0, 0, 0.12)]'>
								<Pagination setPageInfo={setPageInfo} totalCount={filteredOrdersCount} pageInfo={pageInfo}></Pagination>
							</Footer>
						</Layout>
					</>
				)}
			</Layout>
			<LevelContext.Provider
				value={{
					loading,
					modalType,
					props: {
						segment,
						biltyUserTypeId: changedUserId,
						data: {
							record: modalData?.[0],
						},
					},
				}}
			>
				<Outlet />
			</LevelContext.Provider>
		</>
	)
}

export default UserManagementTable
